import { useState, useMemo } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { format, subDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Download, ClipboardCheck, Filter, User, Calendar } from 'lucide-react';

import { FinancePageLayout } from '@/components/finance';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { usePaymentAuditReport, useAppUsers } from '@/hooks/finance/usePaymentAuditReport';

const PAYMENT_TYPES = [
  { value: 'all', label: 'Todos' },
  { value: 'installment', label: 'Parcela' },
  { value: 'single', label: 'À Vista' },
  { value: 'deposit', label: 'Entrada' },
  { value: 'balloon', label: 'Balloon' },
  { value: 'upsell_credit', label: 'Crédito de Upsell' },
];

const formatCurrency = (value: number, currency: string = 'USD') => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency,
  }).format(value);
};

export default function PaymentAuditReport() {
  const [startDate, setStartDate] = useState(safeFormat(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(safeFormat(new Date(), 'yyyy-MM-dd'));
  const [paidByUserId, setPaidByUserId] = useState('all');
  const [paymentType, setPaymentType] = useState('all');

  const { data: payments, isLoading } = usePaymentAuditReport({
    startDate,
    endDate,
    paidByUserId,
    paymentType,
  });

  const { data: users } = useAppUsers();

  const summary = useMemo(() => {
    if (!payments) return { total: 0, count: 0, byUser: {} as Record<string, number> };

    const byUser: Record<string, number> = {};
    payments.forEach(p => {
      const userName = p.paid_by_user || 'Não registrado';
      byUser[userName] = (byUser[userName] || 0) + 1;
    });

    return {
      total: payments.reduce((sum, p) => sum + p.paid_amount, 0),
      count: payments.length,
      byUser,
    };
  }, [payments]);

  const handleExportCSV = () => {
    if (!payments || payments.length === 0) return;

    const headers = [
      'Data Pagamento',
      'Número',
      'Cliente',
      'Deal',
      'Valor',
      'Valor Pago',
      'Método',
      'Marcado Por',
      'Email',
    ];

    const rows = payments.map(p => [
      p.paid_at ? safeFormat(new Date(p.paid_at), 'dd/MM/yyyy HH:mm') : '-',
      p.payment_number || '-',
      p.contact_name,
      p.deal_name,
      p.amount,
      p.paid_amount,
      p.payment_type || '-',
      p.paid_by_user || 'Não registrado',
      p.paid_by_email || '-',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `auditoria-pagamentos-${safeFormat(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  return (
    <FinancePageLayout>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Auditoria de Pagamentos</h1>
        <Button onClick={handleExportCSV} disabled={!payments?.length}>
          <Download className="h-4 w-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Data Início
              </Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Data Fim
              </Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <User className="h-4 w-4" />
                Marcado Por
              </Label>
              <Select value={paidByUserId} onValueChange={setPaidByUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os usuários" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {users?.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.display_name || user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipo de Pagamento</Label>
              <Select value={paymentType} onValueChange={setPaymentType}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total de Pagamentos</CardDescription>
            <CardTitle className="text-2xl">
              {isLoading ? <Skeleton className="h-8 w-24" /> : summary.count}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Valor Total Recebido</CardDescription>
            <CardTitle className="text-2xl">
              {isLoading ? (
                <Skeleton className="h-8 w-32" />
              ) : (
                formatCurrency(summary.total)
              )}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Usuários que Confirmaram</CardDescription>
            <CardTitle className="text-2xl">
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                Object.keys(summary.byUser).length
              )}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data Pagamento</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Deal</TableHead>
                  <TableHead className="text-right">Valor Pago</TableHead>
                  <TableHead>Método</TableHead>
                  <TableHead>Marcado Por</TableHead>
                  <TableHead>Email</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : payments?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Nenhum pagamento encontrado para os filtros selecionados
                    </TableCell>
                  </TableRow>
                ) : (
                  payments?.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>
                        {payment.paid_at
                          ? safeFormat(new Date(payment.paid_at), "dd/MM/yyyy HH:mm", { locale: ptBR })
                          : '-'}
                      </TableCell>
                      <TableCell className="font-medium">{payment.contact_name}</TableCell>
                      <TableCell>{payment.deal_name}</TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(payment.paid_amount, payment.currency)}
                      </TableCell>
                      <TableCell>
                        {payment.payment_type ? (
                          <Badge variant="outline">
                            {PAYMENT_TYPES.find(t => t.value === payment.payment_type)?.label || payment.payment_type}
                          </Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {payment.paid_by_user ? (
                          <span className="font-medium">{payment.paid_by_user}</span>
                        ) : (
                          <span className="text-muted-foreground italic">Não registrado</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {payment.paid_by_email || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </FinancePageLayout>
  );
}
